import { WeaponSlot, type ExplosionDef, type ProjectileDef, type WeaponDef } from '@vm/shared';

const P = WeaponSlot.Primary;
const B = WeaponSlot.Backup;
const ML = WeaponSlot.Melee;
const H = WeaponSlot.Heavy;
const SN = WeaponSlot.Sniper;
const T = WeaponSlot.Tool;

const explosion = (radius: number, damage: number, voxelPower: number, voxelRadius: number, knockback = 10, selfDamage = 0.5): ExplosionDef => ({
  radius,
  damage,
  voxelPower,
  voxelRadius,
  knockback,
  selfDamage,
});

const proj = (p: Partial<ProjectileDef> & Pick<ProjectileDef, 'speed' | 'visual'>): ProjectileDef => ({
  gravity: 0,
  radius: 0.15,
  fuse: 6,
  bounce: 0,
  sticky: false,
  impactDamage: 0,
  explosion: null,
  ...p,
});

type W = Omit<WeaponDef, 'num'>;

/**
 * Weapon catalog. `num` is assigned from list order and goes on the wire, so append new
 * weapons at the end of the list.
 */
const LIST: W[] = [
  // Primary ------------------------------------------------------------------------------
  {
    id: 'brick_rifle', name: 'Brick Rifle', description: 'Reliable full-auto rifle. Chews through wood and brick.',
    slot: P, kind: 'hitscan', rarity: 'common', auto: true, rpm: 600, mag: 30, reserve: 180, reload: 1.9,
    damage: 20, spread: 1.4, aimSpread: 0.45, moveSpread: 1.6, range: 90, falloffStart: 25, falloffEnd: 60, falloffMin: 0.6,
    voxelPower: 4, recoil: 0.55, zoom: 0.8, moveSpeed: 1, equipTime: 0.45, unlockLevel: 1, price: 0, currency: 'coins',
    model: { archetype: 'rifle', primary: 0x4a5058, secondary: 0x8a5a36, accent: 0xe0b030 }, sound: 'rifle', tracer: 0xffc233,
  },
  {
    id: 'buzz_smg', name: 'Buzz SMG', description: 'Fast, light and loud. Best up close.',
    slot: P, kind: 'hitscan', rarity: 'common', auto: true, rpm: 860, mag: 40, reserve: 210, reload: 1.6,
    damage: 15, spread: 1.8, aimSpread: 1.1, moveSpread: 1, range: 45, falloffStart: 12, falloffEnd: 40, falloffMin: 0.65,
    voxelPower: 3, recoil: 0.4, zoom: 0.85, moveSpeed: 1.08, equipTime: 0.35, unlockLevel: 2, price: 600, currency: 'coins',
    model: { archetype: 'smg', primary: 0x2e3238, secondary: 0x3ac8e8, accent: 0xd8e0e8 }, sound: 'smg', tracer: 0x33e6ff,
  },
  {
    id: 'scattergun', name: 'Scattergun', description: 'Pump-action shotgun. Blows holes in cover and anyone behind it.',
    slot: P, kind: 'hitscan', rarity: 'common', auto: false, rpm: 90, mag: 6, reserve: 36, reload: 2.4,
    damage: 18, pellets: 9, spread: 4, aimSpread: 3, moveSpread: 0.5, range: 28, falloffStart: 6, falloffEnd: 18, falloffMin: 0.3,
    voxelPower: 3, recoil: 3.2, zoom: 0.9, moveSpeed: 1, equipTime: 0.5, unlockLevel: 3, price: 800, currency: 'coins',
    model: { archetype: 'shotgun', primary: 0x3e444b, secondary: 0x7a4a2a, accent: 0xc07a4a }, sound: 'shotgun', tracer: 0xff8a1f,
  },
  {
    id: 'pulse_carbine', name: 'Pulse Carbine', description: 'Three-round burst carbine with pinpoint accuracy.',
    slot: P, kind: 'hitscan', rarity: 'rare', auto: false, burst: 3, rpm: 900, mag: 30, reserve: 180, reload: 2,
    damage: 24, spread: 0.9, aimSpread: 0.2, moveSpread: 1.2, range: 100, falloffStart: 35, falloffEnd: 75, falloffMin: 0.65,
    voxelPower: 4, recoil: 0.7, zoom: 0.7, moveSpeed: 1, equipTime: 0.45, unlockLevel: 4, price: 1200, currency: 'coins',
    model: { archetype: 'carbine', primary: 0xdde2ea, secondary: 0x3a6ad8, accent: 0x4af0ff, glow: 0x4af0ff }, sound: 'pulse', tracer: 0x3d8bff,
  },
  {
    id: 'tempest_ar', name: 'Tempest', description: 'High fire-rate assault rifle with a deep magazine.',
    slot: P, kind: 'hitscan', rarity: 'rare', auto: true, rpm: 760, mag: 45, reserve: 200, reload: 2.2,
    damage: 16, spread: 1.3, aimSpread: 0.4, moveSpread: 1.6, range: 85, falloffStart: 22, falloffEnd: 55, falloffMin: 0.55,
    voxelPower: 4, recoil: 0.5, zoom: 0.8, moveSpeed: 1, equipTime: 0.45, unlockLevel: 8, price: 2500, currency: 'coins',
    model: { archetype: 'rifle', primary: 0x3a4a3a, secondary: 0x5a6a4a, accent: 0xf08a2a }, sound: 'rifle', tracer: 0xff5a3a,
  },
  {
    id: 'twin_barrel', name: 'Twin Barrel', description: 'Two shells, two enormous holes.',
    slot: P, kind: 'hitscan', rarity: 'rare', auto: false, rpm: 220, mag: 2, reserve: 30, reload: 1.6,
    damage: 13, pellets: 12, spread: 4, aimSpread: 3, range: 22, falloffStart: 5, falloffEnd: 15, falloffMin: 0.25,
    voxelPower: 4, recoil: 5, zoom: 0.9, moveSpeed: 1, equipTime: 0.5, unlockLevel: 9, price: 2800, currency: 'coins',
    model: { archetype: 'shotgun', primary: 0x6b4a30, secondary: 0x9aa3ad, accent: 0xe8b830 }, sound: 'shotgun_heavy', tracer: 0xffa63a,
  },
  {
    id: 'hornet_smg', name: 'Hornet', description: 'Blistering fire rate, tiny recoil, short range.',
    slot: P, kind: 'hitscan', rarity: 'rare', auto: true, rpm: 1100, mag: 45, reserve: 225, reload: 1.8,
    damage: 12, spread: 1.7, aimSpread: 1, moveSpread: 0.8, range: 40, falloffStart: 12, falloffEnd: 36, falloffMin: 0.65,
    voxelPower: 3, recoil: 0.3, zoom: 0.85, moveSpeed: 1.1, equipTime: 0.3, unlockLevel: 10, price: 3000, currency: 'coins',
    model: { archetype: 'smg', primary: 0xf5d23a, secondary: 0x26262b, accent: 0x26262b }, sound: 'smg', tracer: 0xfff033,
  },
  {
    id: 'ranger_br', name: 'Ranger', description: 'Semi-auto battle rifle. Hard-hitting and precise.',
    slot: P, kind: 'hitscan', rarity: 'epic', auto: false, rpm: 320, mag: 15, reserve: 90, reload: 2.1,
    damage: 38, spread: 0.6, aimSpread: 0.1, moveSpread: 1.4, range: 120, falloffStart: 45, falloffEnd: 90, falloffMin: 0.7,
    voxelPower: 6, recoil: 1.4, zoom: 0.6, moveSpeed: 0.97, equipTime: 0.5, unlockLevel: 12, price: 3800, currency: 'coins',
    model: { archetype: 'dmr', primary: 0x5e412a, secondary: 0x3e444b, accent: 0xe8b830 }, sound: 'rifle_heavy', tracer: 0xa6ff3d,
  },
  {
    id: 'thumper_lmg', name: 'Thumper LMG', description: 'Belt-fed suppression. Deletes walls given time.',
    slot: P, kind: 'hitscan', rarity: 'epic', auto: true, rpm: 600, mag: 80, reserve: 240, reload: 4,
    damage: 22, spread: 1.9, aimSpread: 0.8, moveSpread: 2, range: 90, falloffStart: 30, falloffEnd: 70, falloffMin: 0.6,
    voxelPower: 5, recoil: 0.65, zoom: 0.8, moveSpeed: 0.88, equipTime: 0.7, unlockLevel: 14, price: 4200, currency: 'coins',
    model: { archetype: 'lmg', primary: 0x4a5040, secondary: 0x3e444b, accent: 0xc07a4a }, sound: 'lmg', tracer: 0xff4a2a,
  },
  {
    id: 'wasp_pdw', name: 'Wasp PDW', description: 'Compact personal defense weapon with a punchy cartridge.',
    slot: P, kind: 'hitscan', rarity: 'epic', auto: true, rpm: 750, mag: 30, reserve: 180, reload: 1.7,
    damage: 17, spread: 1.6, aimSpread: 0.7, moveSpread: 0.9, range: 55, falloffStart: 20, falloffEnd: 44, falloffMin: 0.55,
    voxelPower: 4, recoil: 0.45, zoom: 0.82, moveSpeed: 1.06, equipTime: 0.35, unlockLevel: 16, price: 4500, currency: 'coins',
    model: { archetype: 'smg', primary: 0x8a3ac8, secondary: 0x26262b, accent: 0xff5ac8, glow: 0xff5ac8 }, sound: 'smg', tracer: 0xff4fd8,
  },
  {
    id: 'auto_scatter', name: 'Auto Scatter', description: 'Magazine-fed automatic shotgun.',
    slot: P, kind: 'hitscan', rarity: 'epic', auto: true, rpm: 240, mag: 8, reserve: 48, reload: 2.6,
    damage: 11, pellets: 7, spread: 4.5, aimSpread: 3.5, range: 24, falloffStart: 6, falloffEnd: 16, falloffMin: 0.3,
    voxelPower: 3, recoil: 2.4, zoom: 0.9, moveSpeed: 0.97, equipTime: 0.55, unlockLevel: 18, price: 5200, currency: 'coins',
    model: { archetype: 'shotgun', primary: 0x26262b, secondary: 0xd83a3a, accent: 0x9aa3ad }, sound: 'shotgun', tracer: 0xff7a1f,
  },
  {
    id: 'nail_driver', name: 'Nail Driver', description: 'Fires fast heavy nails that punch through wood.',
    slot: P, kind: 'projectile', rarity: 'epic', auto: true, rpm: 470, mag: 40, reserve: 200, reload: 2.3,
    damage: 22, spread: 0.8, aimSpread: 0.3, moveSpread: 1, range: 100, voxelPower: 5, recoil: 0.5, zoom: 0.8,
    projectile: proj({ speed: 80, gravity: 0.15, radius: 0.12, impactDamage: 22, visual: 'nail', fuse: 2 }),
    moveSpeed: 0.97, equipTime: 0.5, unlockLevel: 20, price: 6000, currency: 'coins',
    model: { archetype: 'nailgun', primary: 0xe0b030, secondary: 0x3e444b, accent: 0xd8e0e8 }, sound: 'nail', tracer: 0xd8e0e8,
  },
  {
    id: 'voxel_shredder', name: 'Voxel Shredder', description: 'Spin up, then erase everything in front of you.',
    slot: P, kind: 'hitscan', rarity: 'legendary', auto: true, rpm: 1400, mag: 200, reserve: 400, reload: 5,
    damage: 12, spread: 3.6, aimSpread: 3.0, moveSpread: 1, range: 70, falloffStart: 15, falloffEnd: 38, falloffMin: 0.6,
    voxelPower: 5, recoil: 0.25, charge: 0.45, moveSpeed: 0.82, equipTime: 0.9, unlockLevel: 25, price: 8000, currency: 'coins',
    model: { archetype: 'minigun', primary: 0x3e444b, secondary: 0xd8e0e8, accent: 0xff9a3a, glow: 0xff9a3a }, sound: 'minigun', tracer: 0xff9a3a,
  },

  // Backup ------------------------------------------------------------------------------
  {
    id: 'peashooter', name: 'Peashooter', description: 'Standard sidearm. Always there when you need it.',
    slot: B, kind: 'hitscan', rarity: 'common', auto: false, rpm: 400, mag: 12, reserve: 96, reload: 1.3,
    damage: 24, spread: 0.9, aimSpread: 0.35, moveSpread: 0.8, range: 55, falloffStart: 18, falloffEnd: 40, falloffMin: 0.6,
    voxelPower: 3, recoil: 1, zoom: 0.85, moveSpeed: 1.05, equipTime: 0.25, unlockLevel: 1, price: 0, currency: 'coins',
    model: { archetype: 'pistol', primary: 0x3e444b, secondary: 0x26262b, accent: 0xe0b030 }, sound: 'pistol', tracer: 0xffe066,
  },
  {
    id: 'hand_cannon', name: 'Hand Cannon', description: 'Six rounds of pure authority.',
    slot: B, kind: 'hitscan', rarity: 'rare', auto: false, rpm: 150, mag: 6, reserve: 42, reload: 2.2,
    damage: 55, headMult: 2.2, spread: 0.8, aimSpread: 0.15, moveSpread: 1.2, range: 70, falloffStart: 25, falloffEnd: 55, falloffMin: 0.65,
    voxelPower: 8, recoil: 3.5, zoom: 0.8, moveSpeed: 1.03, equipTime: 0.35, unlockLevel: 5, price: 1500, currency: 'coins',
    model: { archetype: 'revolver', primary: 0xd8e0e8, secondary: 0x7a4a2a, accent: 0xe8b830 }, sound: 'revolver', tracer: 0xffffff,
  },
  {
    id: 'machine_pistol', name: 'Machine Pistol', description: 'A full-auto sidearm for emergencies.',
    slot: B, kind: 'hitscan', rarity: 'rare', auto: true, rpm: 950, mag: 24, reserve: 120, reload: 1.4,
    damage: 13, spread: 2.5, aimSpread: 1.4, moveSpread: 0.8, range: 35, falloffStart: 12, falloffEnd: 30, falloffMin: 0.5,
    voxelPower: 3, recoil: 0.45, zoom: 0.88, moveSpeed: 1.05, equipTime: 0.25, unlockLevel: 7, price: 2000, currency: 'coins',
    model: { archetype: 'pistol', primary: 0x26262b, secondary: 0xd83a3a, accent: 0xd8e0e8 }, sound: 'pistol', tracer: 0x66ff99,
  },
  {
    id: 'flare_gun', name: 'Flare Gun', description: 'Sets targets alight and lights up the night.',
    slot: B, kind: 'projectile', rarity: 'rare', auto: false, rpm: 60, mag: 1, reserve: 16, reload: 1.1,
    damage: 65, spread: 0.4, range: 90, voxelPower: 6, recoil: 2, zoom: 0.9,
    projectile: proj({ speed: 55, gravity: 0.35, radius: 0.18, impactDamage: 65, burn: 15, visual: 'flare', fuse: 4 }),
    moveSpeed: 1.05, equipTime: 0.3, unlockLevel: 11, price: 3200, currency: 'coins',
    model: { archetype: 'flare', primary: 0xf08a2a, secondary: 0x26262b, accent: 0xffe84a, glow: 0xff9a3a }, sound: 'flare', tracer: 0xff9a3a,
  },
  {
    id: 'sawed_off', name: 'Stubby', description: 'A sawed-off shotgun that fits in a pocket.',
    slot: B, kind: 'hitscan', rarity: 'epic', auto: false, rpm: 240, mag: 2, reserve: 24, reload: 1.5,
    damage: 16, pellets: 8, spread: 4, aimSpread: 3, range: 16, falloffStart: 8, falloffEnd: 20, falloffMin: 0.3,
    voxelPower: 3, recoil: 4, zoom: 0.92, moveSpeed: 1.04, equipTime: 0.3, unlockLevel: 13, price: 3600, currency: 'coins',
    model: { archetype: 'shotgun', primary: 0x5e412a, secondary: 0x3e444b, accent: 0xd8e0e8 }, sound: 'shotgun', tracer: 0xffb347,
  },
  {
    id: 'photon_pistol', name: 'Photon Pistol', description: 'Energy sidearm with no damage falloff.',
    slot: B, kind: 'hitscan', rarity: 'epic', auto: false, rpm: 340, mag: 16, reserve: 96, reload: 1.5,
    damage: 24, spread: 0.6, aimSpread: 0.15, moveSpread: 0.6, range: 80,
    voxelPower: 5, recoil: 0.8, zoom: 0.8, moveSpeed: 1.05, equipTime: 0.25, unlockLevel: 15, price: 4400, currency: 'coins',
    model: { archetype: 'laser', primary: 0xdde2ea, secondary: 0x3ac8e8, accent: 0x4af0ff, glow: 0x4af0ff }, sound: 'laser', tracer: 0x4af0ff,
  },

  // Melee -------------------------------------------------------------------------------
  {
    id: 'builders_hammer', name: "Builder's Hammer", description: 'Smashes walls and helmets with equal enthusiasm.',
    slot: ML, kind: 'melee', rarity: 'common', auto: true, rpm: 100, mag: 0, reserve: 0, reload: 0,
    damage: 60, spread: 0, range: 2.3, voxelPower: 30, recoil: 2,
    melee: { range: 2.3, voxelRadius: 0.45, voxelPower: 30, windup: 0.12 },
    moveSpeed: 1.1, equipTime: 0.3, unlockLevel: 1, price: 0, currency: 'coins',
    model: { archetype: 'hammer', primary: 0x8a939d, secondary: 0x8a5a36, accent: 0xe0b030 }, sound: 'hammer',
  },
  {
    id: 'pickaxe', name: 'Pickaxe', description: 'Digs tunnels faster than anything short of explosives.',
    slot: ML, kind: 'melee', rarity: 'common', auto: true, rpm: 150, mag: 0, reserve: 0, reload: 0,
    damage: 56, spread: 0, range: 2.2, voxelPower: 45, recoil: 1.5,
    melee: { range: 2.2, voxelRadius: 0.38, voxelPower: 45, windup: 0.1 },
    moveSpeed: 1.1, equipTime: 0.3, unlockLevel: 3, price: 700, currency: 'coins',
    model: { archetype: 'pickaxe', primary: 0x9aa3ad, secondary: 0x7a5535, accent: 0x3aa88a }, sound: 'hammer',
  },
  {
    id: 'frying_pan', name: 'Frying Pan', description: 'Unreasonably effective against heads.',
    slot: ML, kind: 'melee', rarity: 'rare', auto: true, rpm: 110, mag: 0, reserve: 0, reload: 0,
    damage: 50, headMult: 2.5, spread: 0, range: 2.2, voxelPower: 20, recoil: 2,
    melee: { range: 2.2, voxelRadius: 0.3, voxelPower: 20, windup: 0.1 },
    moveSpeed: 1.12, equipTime: 0.25, unlockLevel: 6, price: 1000, currency: 'coins',
    model: { archetype: 'axe', primary: 0x26262b, secondary: 0x5e412a, accent: 0x8a8a90 }, sound: 'pan',
  },
  {
    id: 'plasma_blade', name: 'Plasma Blade', description: 'A humming energy sword. Long reach, fast swings.',
    slot: ML, kind: 'melee', rarity: 'epic', auto: true, rpm: 95, mag: 0, reserve: 0, reload: 0,
    damage: 70, spread: 0, range: 2.5, voxelPower: 22, recoil: 1.5,
    melee: { range: 2.5, voxelRadius: 0.3, voxelPower: 22, windup: 0.08 },
    moveSpeed: 1.14, equipTime: 0.3, unlockLevel: 12, price: 3500, currency: 'coins',
    model: { archetype: 'sword', primary: 0x3e444b, secondary: 0x4af0ff, accent: 0xd8e0e8, glow: 0x4af0ff }, sound: 'blade',
  },
  {
    id: 'buzz_blade', name: 'Buzz Blade', description: 'A spinning saw that shreds anything it touches.',
    slot: ML, kind: 'melee', rarity: 'epic', auto: true, rpm: 540, mag: 0, reserve: 0, reload: 0,
    damage: 17, spread: 0, range: 2.1, voxelPower: 14, recoil: 0.2,
    melee: { range: 2.1, voxelRadius: 0.26, voxelPower: 14, windup: 0 },
    moveSpeed: 1.05, equipTime: 0.4, unlockLevel: 17, price: 4600, currency: 'coins',
    model: { archetype: 'buzzblade', primary: 0xf08a2a, secondary: 0x26262b, accent: 0xd8e0e8 }, sound: 'saw',
  },
  {
    id: 'katana', name: 'Katana', description: 'Clean, fast, deadly.',
    slot: ML, kind: 'melee', rarity: 'legendary', auto: true, rpm: 90, mag: 0, reserve: 0, reload: 0,
    damage: 62, spread: 0, range: 2.5, voxelPower: 25, recoil: 1.5,
    melee: { range: 2.5, voxelRadius: 0.28, voxelPower: 25, windup: 0.06 },
    moveSpeed: 1.15, equipTime: 0.3, unlockLevel: 22, price: 6500, currency: 'coins',
    model: { archetype: 'katana', primary: 0xd8e0e8, secondary: 0x26262b, accent: 0xd83a3a }, sound: 'blade',
  },

  // Heavy -------------------------------------------------------------------------------
  {
    id: 'rocket_launcher', name: 'Boomstick RPG', description: 'The classic. Craters, collapses and rocket jumps.',
    slot: H, kind: 'projectile', rarity: 'common', auto: false, rpm: 60, mag: 1, reserve: 8, reload: 1.8,
    damage: 0, spread: 0.3, range: 150, voxelPower: 0, recoil: 4, zoom: 0.85,
    projectile: proj({ speed: 34, radius: 0.22, fuse: 5, visual: 'rocket', explosion: explosion(3.6, 115, 70, 1.45, 13, 0.45) }),
    moveSpeed: 0.92, equipTime: 0.6, unlockLevel: 1, price: 0, currency: 'coins',
    model: { archetype: 'rocket', primary: 0x4a5040, secondary: 0x26262b, accent: 0xd83a3a }, sound: 'rocket',
  },
  {
    id: 'mining_laser', name: 'Mining Laser', description: 'An industrial beam that bores tunnels through anything.',
    slot: H, kind: 'beam', rarity: 'common', auto: true, rpm: 600, mag: 150, reserve: 300, reload: 2.4,
    damage: 0, spread: 0, range: 12, voxelPower: 0, recoil: 0,
    beam: { range: 12, dps: 28, voxelDps: 90, voxelRadius: 0.36, visual: 'mining' },
    moveSpeed: 0.95, equipTime: 0.5, unlockLevel: 4, price: 900, currency: 'coins',
    model: { archetype: 'mininglaser', primary: 0xe0b030, secondary: 0x3e444b, accent: 0xff9a3a, glow: 0xff9a3a }, sound: 'mining',
  },
  {
    id: 'bouncer', name: 'Bouncer', description: 'Bouncing grenade launcher with a two-second fuse.',
    slot: H, kind: 'projectile', rarity: 'rare', auto: false, rpm: 100, mag: 6, reserve: 24, reload: 2.8,
    damage: 0, spread: 0.6, range: 80, voxelPower: 0, recoil: 2.5, zoom: 0.9,
    projectile: proj({ speed: 24, gravity: 1, radius: 0.16, fuse: 2, bounce: 0.45, impactDamage: 15, visual: 'grenade', explosion: explosion(3.1, 95, 55, 1.15, 10, 0.5) }),
    moveSpeed: 0.95, equipTime: 0.5, unlockLevel: 7, price: 2200, currency: 'coins',
    model: { archetype: 'grenadelauncher', primary: 0x3a5a3a, secondary: 0x26262b, accent: 0xf5d23a }, sound: 'grenade_launch',
  },
  {
    id: 'dragons_breath', name: "Dragon's Breath", description: 'A flamethrower. Wooden cover does not last.',
    slot: H, kind: 'beam', rarity: 'rare', auto: true, rpm: 600, mag: 100, reserve: 200, reload: 2.6,
    damage: 0, spread: 0, range: 7.5, voxelPower: 0, recoil: 0,
    beam: { range: 7.5, dps: 100, voxelDps: 24, voxelRadius: 0.5, visual: 'flame', burn: 10 },
    moveSpeed: 0.95, equipTime: 0.5, unlockLevel: 10, price: 3000, currency: 'coins',
    model: { archetype: 'flamethrower', primary: 0xd83a3a, secondary: 0x3e444b, accent: 0xf08a2a, glow: 0xff9a3a }, sound: 'flame',
  },
  {
    id: 'plasma_cannon', name: 'Plasma Cannon', description: 'Charge up and release a searing ball of plasma.',
    slot: H, kind: 'projectile', rarity: 'epic', auto: false, rpm: 75, mag: 4, reserve: 16, reload: 2.5, charge: 0.7,
    damage: 0, spread: 0.2, range: 120, voxelPower: 0, recoil: 3,
    projectile: proj({ speed: 46, radius: 0.3, fuse: 4, visual: 'plasma', impactDamage: 20, explosion: explosion(2.6, 90, 55, 1.05, 8, 0.4) }),
    moveSpeed: 0.93, equipTime: 0.6, unlockLevel: 16, price: 4800, currency: 'coins',
    model: { archetype: 'plasma', primary: 0x4a3ab0, secondary: 0xdde2ea, accent: 0xb44aff, glow: 0xb44aff }, sound: 'plasma',
  },
  {
    id: 'mortar', name: 'Siege Mortar', description: 'Lobs enormous shells in a high arc. Levels buildings.',
    slot: H, kind: 'projectile', rarity: 'epic', auto: false, rpm: 45, mag: 1, reserve: 6, reload: 2.6,
    damage: 0, spread: 0.4, range: 120, voxelPower: 0, recoil: 5,
    projectile: proj({ speed: 22, gravity: 1, radius: 0.25, fuse: 8, visual: 'grenade', explosion: explosion(4.2, 125, 85, 1.9, 14, 0.5) }),
    moveSpeed: 0.88, equipTime: 0.7, unlockLevel: 19, price: 5500, currency: 'coins',
    model: { archetype: 'grenadelauncher', primary: 0x5a4a30, secondary: 0x26262b, accent: 0xd83a3a }, sound: 'mortar',
  },
  {
    id: 'tesla_coil', name: 'Tesla Coil', description: 'Arcs lightning between nearby enemies.',
    slot: H, kind: 'beam', rarity: 'legendary', auto: true, rpm: 600, mag: 80, reserve: 160, reload: 2.4,
    damage: 0, spread: 0, range: 14, voxelPower: 0, recoil: 0,
    beam: { range: 14, dps: 80, voxelDps: 12, voxelRadius: 0.25, chain: 3.5, visual: 'tesla' },
    moveSpeed: 0.95, equipTime: 0.5, unlockLevel: 21, price: 6200, currency: 'coins',
    model: { archetype: 'tesla', primary: 0x3e444b, secondary: 0xc07a4a, accent: 0x4a7aff, glow: 0x4a7aff }, sound: 'tesla',
  },
  {
    id: 'railgun', name: 'Railgun', description: 'Charged slug that tunnels straight through walls.',
    slot: H, kind: 'hitscan', rarity: 'legendary', auto: false, rpm: 48, mag: 3, reserve: 15, reload: 2.8, charge: 0.8,
    damage: 125, spread: 0.1, aimSpread: 0, range: 200, voxelPower: 25, voxelRadius: 0.15, penetration: 320,
    recoil: 4, zoom: 0.5, moveSpeed: 0.9, equipTime: 0.7, unlockLevel: 24, price: 7800, currency: 'coins',
    model: { archetype: 'railgun', primary: 0xdde2ea, secondary: 0x3e444b, accent: 0x5aff6a, glow: 0x5aff6a }, sound: 'railgun', tracer: 0x3dff5a,
  },

  // Sniper ------------------------------------------------------------------------------
  {
    id: 'longshot', name: 'Longshot', description: 'Bolt-action sniper. One headshot, one reconstruct.',
    slot: SN, kind: 'hitscan', rarity: 'common', auto: false, rpm: 52, mag: 5, reserve: 30, reload: 2.8,
    damage: 95, headMult: 2.5, spread: 3.5, aimSpread: 0, moveSpread: 2, range: 250, voxelPower: 12, penetration: 40,
    recoil: 3.5, zoom: 0.3, scope: true, moveSpeed: 0.92, equipTime: 0.6, unlockLevel: 3, price: 1000, currency: 'coins',
    model: { archetype: 'sniper', primary: 0x3a4a3a, secondary: 0x7a5535, accent: 0x26262b }, sound: 'sniper', tracer: 0xc58aff,
  },
  {
    id: 'hunter_dmr', name: 'Hunter DMR', description: 'Semi-auto marksman rifle for rapid follow-ups.',
    slot: SN, kind: 'hitscan', rarity: 'rare', auto: false, rpm: 200, mag: 10, reserve: 60, reload: 2.4,
    damage: 48, headMult: 2, spread: 2, aimSpread: 0.05, moveSpread: 1.5, range: 180, voxelPower: 8, penetration: 15,
    recoil: 2, zoom: 0.5, scope: true, moveSpeed: 0.95, equipTime: 0.5, unlockLevel: 9, price: 2600, currency: 'coins',
    model: { archetype: 'dmr', primary: 0x26262b, secondary: 0x4a5058, accent: 0x3ac8e8 }, sound: 'rifle_heavy', tracer: 0x7a9aff,
  },
  {
    id: 'crossbow', name: 'Bolt Crossbow', description: 'Silent, heavy bolts with a gentle arc.',
    slot: SN, kind: 'projectile', rarity: 'epic', auto: false, rpm: 60, mag: 1, reserve: 20, reload: 1.3,
    damage: 100, headMult: 2.5, spread: 0.2, aimSpread: 0, range: 150, voxelPower: 8, recoil: 1.5, zoom: 0.45, scope: true,
    projectile: proj({ speed: 75, gravity: 0.35, radius: 0.1, impactDamage: 100, visual: 'nail', fuse: 4 }),
    moveSpeed: 0.97, equipTime: 0.5, unlockLevel: 14, price: 4000, currency: 'coins',
    model: { archetype: 'crossbow', primary: 0x5e412a, secondary: 0x3e444b, accent: 0xd8e0e8 }, sound: 'crossbow',
  },
  {
    id: 'pierce_rifle', name: 'Pierce', description: 'Anti-materiel rifle. Walls are merely suggestions.',
    slot: SN, kind: 'hitscan', rarity: 'legendary', auto: false, rpm: 56, mag: 4, reserve: 20, reload: 3,
    damage: 85, headMult: 2.4, spread: 4, aimSpread: 0, moveSpread: 2.5, range: 280, voxelPower: 18, voxelRadius: 0.12, penetration: 140,
    recoil: 4.5, zoom: 0.28, scope: true, moveSpeed: 0.9, equipTime: 0.7, unlockLevel: 18, price: 5400, currency: 'coins',
    model: { archetype: 'sniper', primary: 0x26262b, secondary: 0xd8e0e8, accent: 0xd83a3a }, sound: 'sniper_heavy', tracer: 0xff4a5a,
  },

  // Mode-granted ------------------------------------------------------------------------
  {
    id: 'block_gun', name: 'Block Gun', description: 'Places sturdy 0.5 m brick blocks.',
    slot: T, kind: 'builder', rarity: 'common', auto: true, rpm: 360, mag: 0, reserve: 0, reload: 0,
    damage: 0, spread: 0, range: 8, voxelPower: 0, recoil: 0.3,
    builder: { block: 5, material: 'built_brick' },
    moveSpeed: 1, equipTime: 0.25, unlockLevel: 1, price: 0, currency: 'coins', hidden: true,
    model: { archetype: 'blockgun', primary: 0xe0b030, secondary: 0x26262b, accent: 0xc2603a }, sound: 'build',
  },
  {
    id: 'zombie_claws', name: 'Claws', description: 'Corrupted voxel claws.',
    slot: ML, kind: 'melee', rarity: 'common', auto: true, rpm: 100, mag: 0, reserve: 0, reload: 0,
    damage: 25, spread: 0, range: 1.8, voxelPower: 12, recoil: 0,
    melee: { range: 1.8, voxelRadius: 0.3, voxelPower: 12, windup: 0.15 },
    moveSpeed: 1, equipTime: 0.1, unlockLevel: 1, price: 0, currency: 'coins', hidden: true,
    model: { archetype: 'axe', primary: 0x9a3ad8, secondary: 0x9a3ad8, accent: 0x9a3ad8 }, sound: 'claw',
  },
];

export const WEAPONS: WeaponDef[] = LIST.map((w, i) => ({ ...w, num: i + 1 }));
export const WEAPON_BY_ID: Record<string, WeaponDef> = Object.fromEntries(WEAPONS.map((w) => [w.id, w]));
export const WEAPON_BY_NUM: WeaponDef[] = [];
for (const w of WEAPONS) WEAPON_BY_NUM[w.num] = w;

export function weapon(id: string): WeaponDef {
  const w = WEAPON_BY_ID[id];
  if (!w) throw new Error(`Unknown weapon ${id}`);
  return w;
}

export const STARTER_LOADOUT = {
  weapons: ['brick_rifle', 'peashooter', 'builders_hammer', 'rocket_launcher', ''],
  gadget: 'frag',
  pet: 'owlet',
};

/** Weapons players can own (excludes mode-granted tools). */
export const SHOP_WEAPONS = WEAPONS.filter((w) => !w.hidden);
